<template>
  <div class="video-display">
    <!-- Container div: Zoom SDK appends its own <video> element here via attachVideo() -->
    <div ref="containerRef" class="video-container video-player-container" />
    
    <!-- Placeholder for when camera is unavailable -->
    <div v-if="!stream && label" class="camera-unavailable-placeholder">
      <div class="placeholder-icon">📷</div>
      <p class="placeholder-text">Camera unavailable</p>
    </div>
    
    <div v-if="label" class="video-label">{{ label }}</div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue'

interface Props {
  stream: MediaStream | null
  label?: string
  mirrored?: boolean
  muted?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  mirrored: false,
  muted: false,
})

const containerRef = ref<HTMLDivElement>()

async function attachStream(stream: MediaStream | null) {
  if (!containerRef.value) return

  // Clean up container when stream is removed
  if (!stream) {
    containerRef.value.innerHTML = ''
    console.log('[VideoDisplay] Cleared container (null stream)')
    return
  }

  const isZoomStream = (stream as any).__isZoomStream

  if (isZoomStream) {
    const zoomClient = (stream as any).__zoomClient
    const userId = (stream as any).__userId

    if (!zoomClient) {
      console.error('[VideoDisplay] Zoom client not found in stream')
      return
    }

    try {
      await nextTick()
      console.log(`[VideoDisplay] Attaching Zoom video (userId=${userId ?? 'local'})`)

      // ZoomClient.attachVideo() handles startVideo() + attachVideo(userId, quality)
      // and appends the SDK-managed <video> element into the container div.
      await zoomClient.attachVideo(containerRef.value, userId, {
        muted: props.muted,
        mirrored: props.mirrored,
      })

      console.log('[VideoDisplay] Successfully attached Zoom video')
    } catch (error) {
      console.error('[VideoDisplay] Failed to attach Zoom video:', error)
    }
  } else {
    // Standard WebRTC MediaStream — create a video element and set srcObject
    containerRef.value.innerHTML = ''
    const videoEl = document.createElement('video')
    videoEl.autoplay = true
    videoEl.playsInline = true
    videoEl.muted = props.muted
    if (props.mirrored) videoEl.style.transform = 'scaleX(-1)'
    videoEl.style.width = '100%'
    videoEl.style.height = '100%'
    videoEl.style.objectFit = 'cover'
    videoEl.srcObject = stream
    containerRef.value.appendChild(videoEl)
    console.log('[VideoDisplay] Attached WebRTC stream')
  }
}

watch(
  () => props.stream,
  (newStream) => {
    attachStream(newStream)
  },
  { immediate: true }
)

onMounted(() => {
  if (props.stream) {
    attachStream(props.stream)
  }
})
</script>

<style scoped>
.video-display {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.video-container {
  width: 100%;
  height: 100%;
}

.video-label {
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 2;
}

.camera-unavailable-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.8);
  z-index: 1;
}

.placeholder-icon {
  font-size: 64px;
  opacity: 0.5;
}

.placeholder-text {
  color: white;
  font-size: 18px;
  margin-top: 16px;
  opacity: 0.7;
}
</style>
